import { DestroyRef, Injectable, NgZone, inject, DOCUMENT } from '@angular/core';

import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { closeAllOverlays } from '../../../ui';
import { NotificationService } from '../common/notification.service';
import { SessionService } from '../smartclock/session.service';
import { AccessNavigationService, NO_ACCESS_URL } from './access-navigation.service';
import { AccessSnapshot } from './access.model';
import { AccessService } from './access.service';
import { CompanyTerminationService } from './company-termination.service';
import { AuthStorageService } from '../auth/auth-storage.service';
import { isCompanyLockedOut } from '../../../shared/utils/company-access.utils';

/** Recarga periódica mientras la pestaña está abierta. */
export const ACCESS_REFRESH_INTERVAL_MS = 5 * 60 * 1000;
/** Evita ráfagas de recargas (varios 403 seguidos, foco repetido). */
const MIN_GAP_MS = 5000;

export type AccessRefreshReason = 'visibility' | 'interval' | 'forbidden';

/**
 * Reevaluación de permisos sin navegar: recarga `/access/me` al volver a la
 * pestaña, cada 5 minutos y tras un 403. Si el conjunto cambió, reevalúa la
 * ruta actual (si ya no la cumple → `/app/sin-acceso`) y cierra los diálogos
 * abiertos (MatDialog y ventanas del kit), porque una acción a medio camino
 * puede haber dejado de estar permitida.
 *
 * Término de suscripción (`companyAccess.state`): si la persona quedó fuera
 * (empresa bloqueada, o trabajador en modo consulta) va a la página «El
 * servicio terminó»; entrar al modo consulta se trata como un cambio de
 * permisos aunque las claves no cambien; un término programado sólo cambia el
 * banner (reactivo, sin aviso).
 */
@Injectable({ providedIn: 'root' })
export class AccessRefreshService {
  private readonly access = inject(AccessService);
  private readonly termination = inject(CompanyTerminationService);
  private readonly authStorage = inject(AuthStorageService);
  private readonly navigation = inject(AccessNavigationService);
  private readonly session = inject(SessionService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);
  private readonly zone = inject(NgZone);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  private started = false;
  private lastRefreshAt = 0;

  /** Engancha los disparadores (una sola vez, al iniciar la app). */
  start(): void {
    if (this.started) return;
    this.started = true;

    const onVisibility = () => {
      if (this.document.visibilityState === 'visible') {
        this.zone.run(() => this.refresh('visibility'));
      }
    };
    this.document.addEventListener('visibilitychange', onVisibility);

    // El intervalo corre fuera de Angular para no impedir la estabilidad de la zona.
    const timer = this.zone.runOutsideAngular(() =>
      setInterval(() => {
        if (this.document.visibilityState === 'visible') {
          this.zone.run(() => this.refresh('interval'));
        }
      }, ACCESS_REFRESH_INTERVAL_MS),
    );

    this.destroyRef.onDestroy(() => {
      this.document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(timer);
    });
  }

  /**
   * Recarga los permisos y aplica los cambios. Tras un 403 la recarga es
   * normal (si falla, falla cerrado); en segundo plano conserva los permisos
   * si la llamada falla.
   */
  refresh(reason: AccessRefreshReason): void {
    if (!this.session.current) return;
    if (this.access.status() === 'loading' || this.access.status() === 'idle') return;
    const now = Date.now();
    if (reason !== 'forbidden' && now - this.lastRefreshAt < MIN_GAP_MS) return;
    if (reason === 'forbidden' && now - this.lastRefreshAt < 1000) return;
    this.lastRefreshAt = now;

    const before = this.access.snapshot();
    const stateBefore = this.access.companyAccess()?.state ?? null;
    this.access.load({ keepOnError: reason !== 'forbidden' }).subscribe(() => {
      if (!this.session.current) return;
      const companyAccess = this.access.companyAccess();
      // Soporte («Ver como») sigue leyendo una empresa cerrada: el API lo deja en
      // sólo lectura para que el proveedor pueda entregar reportes
      if (
        !this.authStorage.isSupportMode() &&
        isCompanyLockedOut(companyAccess, this.access.role())
      ) {
        this.termination.endFromAccess();
        return;
      }
      const enteredReadOnly = companyAccess?.state === 'read_only' && stateBefore !== 'read_only';
      if (sameAccess(before, this.access.snapshot()) && !enteredReadOnly) return;
      this.applyChange();
    });
  }

  private applyChange(): void {
    this.closeDialogs();
    const current = this.router.url.split('?')[0];
    if (current !== NO_ACCESS_URL && !this.navigation.isCurrentRouteAllowed()) {
      void this.router.navigate([NO_ACCESS_URL], { queryParams: { from: this.router.url } });
      return;
    }
    this.notification.info(this.translate.instant('access.updated'), 4000);
  }

  private closeDialogs(): void {
    this.dialog.closeAll();
    // Las ventanas y listas abiertas del kit.
    closeAllOverlays();
  }
}

function sameSet(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false;
  for (const key of a) if (!b.has(key)) return false;
  return true;
}

function sameAccess(a: AccessSnapshot, b: AccessSnapshot): boolean {
  return (
    a.principal === b.principal &&
    a.role === b.role &&
    sameSet(a.permissions, b.permissions) &&
    sameSet(a.staff, b.staff)
  );
}
